import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Clock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Building2,
  User,
  Mail,
  Phone,
  FileText,
  Shield,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function formatTime(t) {
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${m.toString().padStart(2, "0")} ${ampm}`;
}

function buildCalendarGrid(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const rows = [];
  let week = new Array(firstDay).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) { rows.push(week); week = []; }
  }
  if (week.length) { while (week.length < 7) week.push(null); rows.push(week); }
  return rows;
}

export default function BookDemoPage() {
  const navigate = useNavigate();
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [availableDates, setAvailableDates] = useState(new Set());
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [step, setStep] = useState(1); // 1=date, 2=time, 3=form, 4=confirmed
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", notes: "" });

  const monthKey = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}`;

  const fetchDates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/demo/available-dates`, { params: { month: monthKey } });
      setAvailableDates(new Set(res.data.dates || []));
    } catch { setAvailableDates(new Set()); }
    setLoading(false);
  }, [monthKey]);

  useEffect(() => { fetchDates(); }, [fetchDates]);

  const fetchSlots = useCallback(async (date) => {
    setSlotsLoading(true);
    try {
      const res = await axios.get(`${API}/api/demo/available-slots`, { params: { date } });
      setAvailableSlots(res.data.slots || []);
    } catch { setAvailableSlots([]); }
    setSlotsLoading(false);
  }, []);

  const handleDateClick = (day) => {
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    if (!availableDates.has(dateStr)) return;
    setSelectedDate(dateStr);
    setSelectedTime(null);
    fetchSlots(dateStr);
    setStep(2);
  };

  const handleTimeClick = (time) => {
    setSelectedTime(time);
    setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/demo/book`, {
        date: selectedDate,
        time: selectedTime,
        ...form
      });
      setBookingResult(res.data);
      setStep(4);
      toast.success("Booking confirmed!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed. Please try again.");
    }
    setLoading(false);
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const isPastMonth = viewYear < now.getFullYear() || (viewYear === now.getFullYear() && viewMonth < now.getMonth());

  const grid = buildCalendarGrid(viewYear, viewMonth);

  const formattedSelectedDate = selectedDate ? (() => {
    const d = new Date(selectedDate + "T12:00:00");
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  })() : "";

  return (
    <div className="bg-[#0a1628] min-h-screen" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="max-w-3xl mx-auto px-4 py-10 md:py-16">

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => step > 1 && step < 4 ? setStep(s => s - 1) : navigate("/partners/verify")}
            className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#52BFB8] transition-colors mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {step > 1 && step < 4 ? "Back" : "Partners"}
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#52BFB8]/10 border border-[#52BFB8]/20 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-[#52BFB8]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                Schedule a Demo
              </h1>
              <p className="text-sm text-slate-400">30-minute session with our partnerships team</p>
            </div>
          </div>
        </div>

        {/* Progress */}
        {step < 4 && (
          <div className="flex items-center gap-2 mb-8" data-testid="booking-progress">
            {["Date", "Time", "Details"].map((label, i) => (
              <div key={i} className="flex items-center gap-2 flex-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                  step > i + 1 ? "bg-[#52BFB8] text-[#0a1628]" :
                  step === i + 1 ? "bg-[#52BFB8]/20 text-[#52BFB8] border border-[#52BFB8]/40" :
                  "bg-white/5 text-slate-600 border border-white/10"
                }`}>{step > i + 1 ? <CheckCircle2 className="w-4 h-4" /> : i + 1}</div>
                <span className={`text-xs font-medium hidden sm:inline ${step >= i + 1 ? "text-slate-300" : "text-slate-600"}`}>{label}</span>
                {i < 2 && <div className={`flex-1 h-px ${step > i + 1 ? "bg-[#52BFB8]/40" : "bg-white/10"}`} />}
              </div>
            ))}
          </div>
        )}

        {/* Step 1: Calendar */}
        {step === 1 && (
          <div className="rounded-2xl bg-[#111d32]/50 border border-white/[0.08] p-5 md:p-8" data-testid="calendar-step">
            <div className="flex items-center justify-between mb-6">
              <button onClick={prevMonth} disabled={isPastMonth} className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-testid="prev-month-btn">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                {MONTHS[viewMonth]} {viewYear}
              </h2>
              <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors" data-testid="next-month-btn">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map(d => (
                <div key={d} className="text-center text-xs font-medium text-slate-500 py-1">{d}</div>
              ))}
            </div>

            {/* Calendar grid */}
            {loading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#52BFB8] animate-spin" /></div>
            ) : (
              <div className="space-y-1">
                {grid.map((week, wi) => (
                  <div key={wi} className="grid grid-cols-7 gap-1">
                    {week.map((day, di) => {
                      if (!day) return <div key={di} />;
                      const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                      const isAvailable = availableDates.has(dateStr);
                      const isToday = dateStr === todayStr;
                      const isPast = dateStr < todayStr;
                      return (
                        <button
                          key={di}
                          onClick={() => isAvailable && handleDateClick(day)}
                          disabled={!isAvailable || isPast}
                          data-testid={`cal-day-${day}`}
                          className={`relative aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all ${
                            isAvailable
                              ? "bg-[#52BFB8]/10 text-[#52BFB8] hover:bg-[#52BFB8]/20 hover:scale-105 cursor-pointer border border-[#52BFB8]/20"
                              : isPast
                                ? "text-slate-700 cursor-not-allowed"
                                : "text-slate-500 cursor-default"
                          }`}
                        >
                          {day}
                          {isToday && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#52BFB8]" />}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            {!loading && availableDates.size === 0 && (
              <p className="text-center text-sm text-slate-500 mt-6">No available dates this month. Try the next month.</p>
            )}
          </div>
        )}

        {/* Step 2: Time Slots */}
        {step === 2 && (
          <div className="rounded-2xl bg-[#111d32]/50 border border-white/[0.08] p-5 md:p-8" data-testid="time-step">
            <div className="flex items-center gap-2 mb-6">
              <CalendarDays className="w-4 h-4 text-[#52BFB8]" />
              <span className="text-sm text-slate-300 font-medium">{formattedSelectedDate}</span>
            </div>
            <h3 className="text-base font-semibold text-white mb-4" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Select a time <span className="text-slate-500 font-normal text-sm">(EST / Nassau)</span>
            </h3>
            {slotsLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-[#52BFB8] animate-spin" /></div>
            ) : availableSlots.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">No available slots for this date.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {availableSlots.map((slot) => (
                  <button
                    key={slot}
                    onClick={() => handleTimeClick(slot)}
                    data-testid={`time-slot-${slot}`}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl text-sm font-medium transition-all border ${
                      selectedTime === slot
                        ? "bg-[#52BFB8]/20 text-[#52BFB8] border-[#52BFB8]/40"
                        : "bg-[#0a1628]/60 text-slate-300 border-white/[0.06] hover:border-[#52BFB8]/30 hover:text-[#52BFB8]"
                    }`}
                  >
                    <Clock className="w-3.5 h-3.5" />
                    {formatTime(slot)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Contact Form */}
        {step === 3 && (
          <div className="rounded-2xl bg-[#111d32]/50 border border-white/[0.08] p-5 md:p-8" data-testid="form-step">
            <div className="flex items-center gap-4 mb-6 pb-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <CalendarDays className="w-4 h-4 text-[#52BFB8]" />
                {formattedSelectedDate}
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Clock className="w-4 h-4 text-[#52BFB8]" />
                {formatTime(selectedTime)} EST
              </div>
            </div>

            <h3 className="text-base font-semibold text-white mb-5" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Your details
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><User className="w-3 h-3" /> Full Name *</label>
                <input
                  type="text" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-[#0a1628] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-[#52BFB8]/50 focus:ring-1 focus:ring-[#52BFB8]/30 outline-none transition-colors"
                  placeholder="John Rolle" data-testid="input-name"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><Mail className="w-3 h-3" /> Email *</label>
                <input
                  type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full bg-[#0a1628] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-[#52BFB8]/50 focus:ring-1 focus:ring-[#52BFB8]/30 outline-none transition-colors"
                  placeholder="john@company.com" data-testid="input-email"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Company *</label>
                <input
                  type="text" required value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                  className="w-full bg-[#0a1628] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-[#52BFB8]/50 focus:ring-1 focus:ring-[#52BFB8]/30 outline-none transition-colors"
                  placeholder="ABC Financial" data-testid="input-company"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><Phone className="w-3 h-3" /> Phone <span className="text-slate-600">(optional)</span></label>
                <input
                  type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full bg-[#0a1628] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-[#52BFB8]/50 focus:ring-1 focus:ring-[#52BFB8]/30 outline-none transition-colors"
                  placeholder="(242) 000-0000" data-testid="input-phone"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><FileText className="w-3 h-3" /> Notes <span className="text-slate-600">(optional)</span></label>
                <textarea
                  value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3}
                  className="w-full bg-[#0a1628] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-[#52BFB8]/50 focus:ring-1 focus:ring-[#52BFB8]/30 outline-none transition-colors resize-none"
                  placeholder="What products are you interested in?" data-testid="input-notes"
                />
              </div>

              <Button
                type="submit" disabled={loading}
                className="w-full bg-[#52BFB8] hover:bg-[#3ea8a1] text-[#0a1628] font-semibold py-6 text-base rounded-lg shadow-[0_0_24px_rgba(82,191,184,0.3)] disabled:opacity-50"
                data-testid="confirm-booking-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirm Booking
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && (
          <div className="rounded-2xl bg-[#111d32]/50 border border-white/[0.08] p-6 md:p-10 text-center" data-testid="confirmation-step">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#52BFB8]/15 flex items-center justify-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-[#52BFB8]" />
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Booking Confirmed
            </h2>
            <p className="text-slate-400 mb-6 text-sm">
              A confirmation has been sent to <span className="text-[#52BFB8]">{form.email}</span>
            </p>

            <div className="inline-flex flex-col gap-3 bg-[#0a1628] rounded-xl p-5 border border-white/[0.06] text-left mb-8">
              <div className="flex items-center gap-3">
                <CalendarDays className="w-4 h-4 text-[#52BFB8]" />
                <span className="text-sm text-white">{formattedSelectedDate}</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-[#52BFB8]" />
                <span className="text-sm text-white">{formatTime(selectedTime)} EST — 30 min</span>
              </div>
              {bookingResult?.booking_id && (
                <div className="flex items-center gap-3">
                  <Shield className="w-4 h-4 text-slate-500" />
                  <span className="text-xs text-slate-500">Ref: {bookingResult.booking_id}</span>
                </div>
              )}
            </div>

            <p className="text-xs text-slate-500 mb-6">
              Our partnerships team will follow up with meeting details before your session.
            </p>

            <Button
              onClick={() => navigate("/partners/verify")}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5"
              data-testid="back-to-partners-btn"
            >
              Back to Partners
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
